import React, { useState, useCallback } from "react";
import { X, ExternalLink, Tv, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  detectDevice,
  resolveWatchDestination,
  getConfiguredPlatforms,
  WATCH_DESTINATIONS,
} from "@/lib/watchRouting";
import { track, EVENTS } from "@/lib/analytics";

/**
 * Network-level Watch Now CTA with device-aware routing.
 * - iOS/iPadOS  → Apple App Store (configured)
 * - Android/Roku/FireTV/Desktop → configured URL if present, else modal
 * - Unknown → modal
 *
 * NOTE: Do NOT use this for content-level "Play" or "Watch Episode" buttons.
 */
export default function WatchNowButton({
  className = "",
  variant = "primary", // "primary" | "ghost"
  label = "Watch Now",
  icon: Icon = Play,
  testId = "watch-now-btn",
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const onClick = useCallback(() => {
    const device = detectDevice();
    const dest = resolveWatchDestination(device);
    track(EVENTS.WATCH_NOW_CLICKED, { device, hasDirectDestination: Boolean(dest) });

    if (dest) {
      if (dest.key === "APPLE_APP_URL") {
        track(EVENTS.APPLE_APP_STORE_CLICKED, { device });
      }
      window.open(dest.url, "_blank", "noopener,noreferrer");
      return;
    }
    track(EVENTS.WATCH_PLATFORM_MODAL_OPENED, { device });
    setModalOpen(true);
  }, []);

  const baseClasses =
    variant === "primary"
      ? "ycn-btn-primary inline-flex items-center gap-2 px-5 py-2 md:px-6 md:py-2.5 rounded-full font-semibold text-white text-sm transition-all"
      : "inline-flex items-center gap-2 px-5 py-2.5 rounded-full ycn-glass hover:bg-white/15 font-semibold text-white text-sm transition";

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={cn(baseClasses, className)}
      >
        <Icon size={16} fill={variant === "primary" ? "currentColor" : "none"} /> {label}
      </button>
      <WatchYCNModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

function WatchYCNModal({ open, onClose }) {
  if (!open) return null;
  const platforms = getConfiguredPlatforms();

  return (
    <div
      data-testid="watch-ycn-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="watch-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative ycn-glass rounded-3xl w-full max-w-lg p-8 md:p-10 border border-white/10 shadow-[0_30px_80px_-20px_rgba(124,58,237,0.5)] ycn-fade-up">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="watch-modal-close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="text-[10px] uppercase tracking-[0.35em] text-white/60 mb-3">
          <Tv size={12} className="inline mr-1.5" /> Where to Watch
        </div>
        <h3 id="watch-modal-title" className="font-display text-3xl md:text-4xl font-black leading-tight">
          Watch YCN <span className="ycn-gradient-text">Your Way</span>
        </h3>
        <p className="mt-3 text-white/70 text-sm leading-relaxed">
          Stream Your Channel Network on supported devices and platforms.
        </p>

        <div className="mt-6 space-y-2.5" data-testid="watch-modal-platforms">
          {platforms.length === 0 && (
            <div className="ycn-glass rounded-2xl p-5 text-center text-white/70 text-sm">
              More platforms coming soon. Check back shortly.
            </div>
          )}
          {platforms.map((p) => (
            <a
              key={p.key}
              href={WATCH_DESTINATIONS[p.key]}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`watch-modal-${p.key.toLowerCase()}`}
              onClick={() => {
                if (p.key === "APPLE_APP_URL") track(EVENTS.APPLE_APP_STORE_CLICKED, { source: "modal" });
              }}
              className="group flex items-center justify-between gap-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/25 px-5 py-4 transition"
            >
              <div>
                <div className="font-semibold text-white text-[15px]">{p.label}</div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">{p.sub}</div>
              </div>
              <ExternalLink size={16} className="text-white/60 group-hover:text-white" />
            </a>
          ))}
        </div>

        <p className="mt-6 text-[11px] text-white/40 text-center">
          Your Channel Network — Your Story. Your Voice. Your Channel.
        </p>
      </div>
    </div>
  );
}
